import type { ProductSurface } from '@lib/enums/product-surface.enum'
import * as React from 'react'
import { Outlet } from 'react-router'
import { useAuth } from '@/auth/AuthContext'
import { useDashboardScope } from '@/dashboard/DashboardScope'
import { ReadOnlyBanner } from '@/dashboard/ReadOnlyBanner'
import type { EnvelopeCacheKey } from '@/hooks/queryKeys'
import { EnvelopeService, opsEnvelopeService } from '@/services/EnvelopeService'

/**
 * What the envelope pages need to know about where they are: which API to
 * call, where links go, and what this viewer may do. The same list and detail
 * pages serve the ops console and every tenant dashboard (including staff
 * view-as) without knowing which.
 */
export interface EnvelopeAccessValue {
  surface: ProductSurface
  service: EnvelopeService
  /** Separates cached envelopes between surfaces and tenants. */
  cacheKey: EnvelopeCacheKey
  listPath: string
  detailPath: (id: string) => string
  /** Remind, extend, and void. */
  canAct: boolean
  canDownload: boolean
  /** Shown above the page (a suspended or view-as notice), if any. */
  notice: React.ReactNode
  /** Where an envelope's captured emails are (test mode in the dashboard only). */
  mailboxPath: ((envelopeId: string) => string) | null
  /** Where an envelope's webhook deliveries are (the dashboard only). */
  webhooksPath: ((envelopeId: string) => string) | null
}

const EnvelopeAccessContext = React.createContext<EnvelopeAccessValue | null>(null)

export function useEnvelopeAccess(): EnvelopeAccessValue {
  const access = React.use(EnvelopeAccessContext)
  if (access === null) {
    throw new Error('useEnvelopeAccess must be used within an envelope access provider')
  }
  return access
}

/** Staff: every tenant. ADMIN acts and downloads; SUPPORT reads. */
export function OpsEnvelopeAccess() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN'
  const value = React.useMemo<EnvelopeAccessValue>(
    () => ({
      surface: 'ops',
      service: opsEnvelopeService,
      cacheKey: 'ops',
      listPath: '/ops/envelopes',
      detailPath: (id) => `/ops/envelopes/${id}`,
      canAct: isAdmin,
      canDownload: isAdmin,
      notice: null,
      mailboxPath: null,
      webhooksPath: null,
    }),
    [isAdmin],
  )
  return (
    <EnvelopeAccessContext value={value}>
      <Outlet />
    </EnvelopeAccessContext>
  )
}

/**
 * One tenant's envelopes. Owners and developers act unless the dashboard is
 * read-only (suspended, or staff viewing as the tenant). They still download
 * while suspended (the API allows it: they're entitled to their signed files);
 * staff viewing as the tenant never download here.
 */
export function DashboardEnvelopeAccess() {
  const scope = useDashboardScope()
  const servicer = scope.role === 'owner' || scope.role === 'developer'
  const canAct = servicer && !scope.readOnly
  const canDownload = servicer && scope.readOnlyReason !== 'view-as'
  const value = React.useMemo<EnvelopeAccessValue>(() => {
    const listPath = `${scope.basePath}/envelopes`
    return {
      surface: 'dashboard',
      service: new EnvelopeService(
        scope.client,
        `/dashboard/tenants/${encodeURIComponent(scope.tenantId)}/modes/${scope.mode}/envelopes`,
      ),
      cacheKey: `${scope.cacheKey}:${scope.tenantId}:${scope.mode}`,
      listPath,
      detailPath: (id) => `${listPath}/${id}`,
      canAct,
      canDownload,
      notice: <ReadOnlyBanner />,
      mailboxPath:
        scope.mode === 'test' ? (id) => `${scope.basePath}/mailbox?envelopeId=${encodeURIComponent(id)}` : null,
      webhooksPath: (id) => `${scope.basePath}/webhooks?envelopeId=${encodeURIComponent(id)}`,
    }
  }, [scope.basePath, scope.client, scope.tenantId, scope.cacheKey, scope.mode, canAct, canDownload])
  return (
    <EnvelopeAccessContext value={value}>
      <Outlet />
    </EnvelopeAccessContext>
  )
}
